import {useEffect, useState, useRef} from 'react';
import {Form, Switch, Upload, Input} from 'antd';
import {useMutation} from '@apollo/client';
import {UPDATE_APP_CONFIGURATION} from 'graphql/queries/mobileSettings';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {toast} from 'react-toastify';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {UploadDocumentToBucket} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import {Button} from '@dabafinance/react-components';
import styles from './referralChannel.module.scss';
import {UploadOutlined} from '@ant-design/icons';
import plusIcon from 'images/plus-circle.png';
import deleteIcon from 'images/delete.svg';

const {Dragger} = Upload;

// Default channels with static icon URLs
const DEFAULT_CHANNELS = [
  {
    key: 'FACEBOOK',
    label: 'Facebook',
    defaultIconUrl:
      'https://dabafinancialinstruments-nonprod.s3.us-east-1.amazonaws.com/channels/daba-finance-invest-africa-1764767578028-facebookpng',
  },
  {
    key: 'TIKTOK',
    label: 'TikTok',
    defaultIconUrl:
      'https://dabafinancialinstruments-nonprod.s3.us-east-1.amazonaws.com/channels/daba-finance-invest-africa-1764767711658-tiktokpng',
  },
  {
    key: 'INSTAGRAM',
    label: 'Instagram',
    defaultIconUrl:
      'https://dabafinancialinstruments-nonprod.s3.us-east-1.amazonaws.com/channels/daba-finance-invest-africa-1764767738964-instagrampng',
  },
  {
    key: 'LINKEDIN',
    label: 'LinkedIn',
    defaultIconUrl:
      'https://dabafinancialinstruments-nonprod.s3.us-east-1.amazonaws.com/channels/daba-finance-invest-africa-1764767753374-linkedinpng',
  },
  {
    key: 'YOUTUBE',
    label: 'YouTube',
    defaultIconUrl:
      'https://dabafinancialinstruments-nonprod.s3.us-east-1.amazonaws.com/channels/daba-finance-invest-africa-1764767768978-youtubepng',
  },
  {
    key: 'FRIEND',
    label: 'A Friend',
    defaultIconUrl:
      'https://dabafinancialinstruments-nonprod.s3.us-east-1.amazonaws.com/channels/daba-finance-invest-africa-1764767794397-friendpng',
  },
];

// Helper function to parse channel (could be JSON string or object)
const parseChannel = channel => {
  if (!channel) return {EN: '', FR: ''};
  if (typeof channel === 'string') {
    try {
      return JSON.parse(channel);
    } catch {
      // If parsing fails, assume it's the old format (just a string)
      return {EN: channel, FR: channel};
    }
  }
  return channel;
};

// Helper function to get icon with priority: uploaded > API > default
const getIconWithPriority = (uploadedIcon, apiIcon, defaultIcon) => {
  // Priority: uploaded > API > default
  if (uploadedIcon && uploadedIcon !== defaultIcon) {
    return uploadedIcon;
  }
  if (apiIcon && apiIcon !== defaultIcon) {
    return apiIcon;
  }
  return defaultIcon;
};

// Helper function to extract icon URL from various formats
// Handles: string URL, file object with response, or nested file structure
const extractIconUrl = icon => {
  if (!icon) return '';

  // If it's already a string URL, return it
  if (typeof icon === 'string') {
    return icon;
  }

  // If it's an object, try to extract the URL from various possible structures
  if (typeof icon === 'object') {
    // Check for file.response.url (Ant Design Upload response structure)
    if (icon.file?.response?.url) {
      return icon.file.response.url;
    }
    // Check for response.url (direct response structure)
    if (icon.response?.url) {
      return icon.response.url;
    }
    // Check for direct url property
    if (icon.url) {
      return icon.url;
    }
  }

  return '';
};

export const ReferralChannels = ({data, refetch}) => {
  const [form] = Form.useForm();
  const [openFinishModal, setOpenFinishModal] = useState(false);
  const [uploadingIcons, setUploadingIcons] = useState({});
  const [defaultChannelStates, setDefaultChannelStates] = useState({});
  const fileInputRefs = useRef({});

  const isDev = process.env.REACT_APP_API_BASE_URL.includes('.daba-dev.');
  const bucket = isDev
    ? 'dabafinancialinstruments-nonprod'
    : 'dabafinancialinstruments-prod';

  const [updateAppConfig, {loading: loadingUpdate}] = useMutation(
    UPDATE_APP_CONFIGURATION,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [translateText, {loading: loadingTranslation}] =
    useMutation(TRANSLATE_TEXT);

  useEffect(() => {
    if (data?.referralChannels && Array.isArray(data.referralChannels)) {
      const states = {};
      const defaultFormValues = {};
      const additionalChannels = [];

      // Separate default channels from additional channels
      const defaultChannelLabels = DEFAULT_CHANNELS.map(ch => ch.label);
      const defaultChannelsInData = data.referralChannels.filter(ch => {
        const parsedChannel = parseChannel(ch.channel);
        return defaultChannelLabels.includes(parsedChannel.EN);
      });
      const additionalChannelsInData = data.referralChannels.filter(ch => {
        const parsedChannel = parseChannel(ch.channel);
        return !defaultChannelLabels.includes(parsedChannel.EN);
      });

      // Set up default channels
      DEFAULT_CHANNELS.forEach(channel => {
        const existingChannel = defaultChannelsInData.find(ch => {
          const parsedChannel = parseChannel(ch.channel);
          return parsedChannel.EN === channel.label;
        });

        if (existingChannel) {
          states[channel.key] = true;
          const parsedChannel = parseChannel(existingChannel.channel);
          // Store API icon separately, uploaded icon will be in form values
          defaultFormValues[`${channel.key}_icon_api`] = existingChannel.icon;
          defaultFormValues[`${channel.key}_icon`] = ''; // Will be set if user uploads
          defaultFormValues[`${channel.key}_channel_EN`] =
            parsedChannel.EN || channel.label;
          defaultFormValues[`${channel.key}_channel_FR`] =
            parsedChannel.FR || channel.label;
        } else {
          states[channel.key] = false;
          defaultFormValues[`${channel.key}_icon_api`] = '';
          defaultFormValues[`${channel.key}_icon`] = '';
          defaultFormValues[`${channel.key}_channel_EN`] = channel.label;
          defaultFormValues[`${channel.key}_channel_FR`] = channel.label;
        }
      });

      // Set up additional channels
      additionalChannelsInData.forEach(channel => {
        const parsedChannel = parseChannel(channel.channel);
        additionalChannels.push({
          channel: {
            EN: parsedChannel.EN || '',
            FR: parsedChannel.FR || '',
          },
          icon: channel.icon,
        });
      });

      setDefaultChannelStates(states);
      form.setFieldsValue({
        defaultChannels: defaultFormValues,
        additionalChannels: additionalChannels,
      });
    } else {
      // Initialize all default channels as inactive with default values
      const states = {};
      const defaultFormValues = {};
      DEFAULT_CHANNELS.forEach(channel => {
        states[channel.key] = false;
        defaultFormValues[`${channel.key}_icon_api`] = '';
        defaultFormValues[`${channel.key}_icon`] = '';
        defaultFormValues[`${channel.key}_channel_EN`] = channel.label;
        defaultFormValues[`${channel.key}_channel_FR`] = channel.label;
      });
      setDefaultChannelStates(states);
      form.setFieldsValue({
        defaultChannels: defaultFormValues,
        additionalChannels: [],
      });
    }
  }, [data, form]);

  const handleDefaultChannelToggle = (channelKey, checked) => {
    setDefaultChannelStates(prev => ({
      ...prev,
      [channelKey]: checked,
    }));
  };

  const handleTranslate = async (channelKey, isDefault = true) => {
    let englishText;
    if (isDefault) {
      englishText =
        form.getFieldValue('defaultChannels')?.[`${channelKey}_channel_EN`];
    } else {
      const additionalChannels = form.getFieldValue('additionalChannels') || [];
      const channel = additionalChannels[channelKey];
      englishText = channel?.channel?.EN;
    }

    if (!englishText?.trim()) {
      toast.error('Please enter text in English first');
      return;
    }

    try {
      const {data: translateData} = await translateText({
        variables: {
          sourceLang: 'EN',
          targetLang: 'FR',
          sourceText: englishText,
        },
      });

      if (
        translateData?.translateText?.statusCode === 400 ||
        translateData?.translateText?.__typename === 'Error'
      ) {
        toast.error(
          translateData?.translateText?.message || 'Translation failed',
        );
      } else {
        const translatedText = translateData?.translateText?.targetText || '';
        if (isDefault) {
          const currentValues = form.getFieldValue('defaultChannels') || {};
          form.setFieldsValue({
            defaultChannels: {
              ...currentValues,
              [`${channelKey}_channel_FR`]: translatedText,
            },
          });
        } else {
          // Get current channel values to preserve icon and other fields
          const currentChannel =
            form.getFieldValue(['additionalChannels', channelKey]) || {};
          form.setFieldValue(['additionalChannels', channelKey], {
            ...currentChannel,
            channel: {
              ...currentChannel.channel,
              EN: currentChannel.channel?.EN || '',
              FR: translatedText,
            },
          });
        }
        toast.success('Translated Successfully');
      }
    } catch (error) {
      toast.error('Translation failed');
    }
  };

  const handleIconUpload = async (channelKey, file, isDefault = true) => {
    try {
      const uploadKey = isDefault ? `default_${channelKey}` : channelKey;
      setUploadingIcons(prev => ({...prev, [uploadKey]: true}));

      const fileLink = await UploadDocumentToBucket(
        file,
        () => {},
        bucket,
        true,
        'channels',
      );

      if (fileLink) {
        if (isDefault) {
          const currentValues = form.getFieldValue('defaultChannels') || {};
          form.setFieldsValue({
            defaultChannels: {
              ...currentValues,
              [`${channelKey}_icon`]: fileLink,
            },
          });
        } else {
          // For additional channels, use setFieldValue with Form.List path
          form.setFieldValue(
            ['additionalChannels', channelKey, 'icon'],
            fileLink,
          );
        }
        toast.success('Icon uploaded successfully');
        return fileLink;
      } else {
        toast.error('Failed to upload icon');
        return null;
      }
    } catch (error) {
      console.error(`[${channelKey}] Upload error:`, error);
      toast.error('Failed to upload icon');
      return null;
    } finally {
      const uploadKey = isDefault ? `default_${channelKey}` : channelKey;
      setUploadingIcons(prev => ({...prev, [uploadKey]: false}));
    }
  };

  const handleAdditionalChannelUpload = async (options, fieldName) => {
    const {file, onSuccess, onError} = options;
    const fileObj = file.originFileObj || file;

    if (!fileObj) {
      toast.error('No file selected');
      onError?.(new Error('No file selected'));
      return;
    }

    try {
      const fileLink = await handleIconUpload(fieldName, fileObj, false);
      if (fileLink) {
        onSuccess?.({url: fileLink}, file);
      } else {
        onError?.(new Error('Upload failed'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload icon');
      onError?.(error);
    }
  };

  const handleLogoClick = channelKey => {
    const input = fileInputRefs.current[channelKey];
    if (input) {
      input.click();
    }
  };

  const handleFileChange = async (channelKey, event) => {
    const file = event.target.files?.[0];
    if (file) {
      await handleIconUpload(channelKey, file, true);
      // Reset input
      event.target.value = '';
    }
  };

  const handleSubmit = () => {
    const formValues = form.getFieldsValue();
    const referralChannels = [];

    // Add active default channels
    DEFAULT_CHANNELS.forEach(channel => {
      if (defaultChannelStates[channel.key]) {
        const uploadedIcon =
          formValues?.defaultChannels?.[`${channel.key}_icon`] || '';
        const apiIcon =
          formValues?.defaultChannels?.[`${channel.key}_icon_api`] || '';
        const icon = getIconWithPriority(
          uploadedIcon,
          apiIcon,
          channel.defaultIconUrl,
        );

        const channelEN =
          formValues?.defaultChannels?.[`${channel.key}_channel_EN`] ||
          channel.label;
        const channelFR =
          formValues?.defaultChannels?.[`${channel.key}_channel_FR`] ||
          channel.label;

        referralChannels.push({
          channel: {EN: channelEN, FR: channelFR},
          icon: icon,
        });
      }
    });

    // Add additional channels
    const additionalChannels = formValues?.additionalChannels || [];
    additionalChannels.forEach(channel => {
      if (channel?.channel?.EN) {
        const iconUrl = extractIconUrl(channel?.icon);
        if (iconUrl) {
          referralChannels.push({
            channel: channel.channel,
            icon: iconUrl,
          });
        }
      }
    });

    updateAppConfig({
      variables: {
        data: {
          referralChannels,
        },
      },
    })
      .then(({data: {updateAppConfig}}) => {
        if (updateAppConfig.__typename === 'Error') {
          toast.error(updateAppConfig.message);
        } else {
          toast.success('Updated Successfully');
          setOpenFinishModal(false);
          if (refetch) {
            refetch();
          }
        }
      })
      .catch(error => toast.error(error.message || 'Update failed'));
  };

  return (
    <div className={styles.body}>
      <div className={styles.header}>
        <h2>Referral Channels</h2>
        <p>
          Configure which referral channels are visible in the app. Default
          channels can be activated/deactivated. You can also add custom
          channels. Only active channels will be displayed to users.
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={() => setOpenFinishModal(true)}>
        {/* Default Channels Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Default Channels</h3>
          <div className={styles.channelsList}>
            {DEFAULT_CHANNELS.map(channel => {
              const isSelected = defaultChannelStates[channel.key] || false;
              const uploadedIcon =
                form.getFieldValue('defaultChannels')?.[
                  `${channel.key}_icon`
                ] || '';
              const apiIcon =
                form.getFieldValue('defaultChannels')?.[
                  `${channel.key}_icon_api`
                ] || '';
              const displayIcon = getIconWithPriority(
                uploadedIcon,
                apiIcon,
                channel.defaultIconUrl,
              );
              const isUploading =
                uploadingIcons[`default_${channel.key}`] || false;

              return (
                <div key={channel.key} className={styles.channelCard}>
                  <div className={styles.channelHeader}>
                    <div className={styles.channelInfo}>
                      <div className={styles.iconWrapper}>
                        <input
                          ref={el => (fileInputRefs.current[channel.key] = el)}
                          type="file"
                          accept="image/*"
                          style={{display: 'none'}}
                          onChange={e => handleFileChange(channel.key, e)}
                        />
                        <img
                          src={displayIcon}
                          alt={channel.label}
                          className={styles.channelIcon}
                          onClick={() => handleLogoClick(channel.key)}
                          title="Click to upload icon"
                        />
                        {isUploading && (
                          <div className={styles.uploadingOverlay}>
                            <UploadOutlined />
                          </div>
                        )}
                      </div>
                      <h3>{channel.label}</h3>
                    </div>
                    <Switch
                      checked={isSelected}
                      onChange={checked =>
                        handleDefaultChannelToggle(channel.key, checked)
                      }
                    />
                  </div>

                  {isSelected && (
                    <div className={styles.channelContent}>
                      <div className={styles.languagesContainer}>
                        <div className={styles.languageSection}>
                          <div className={styles.languageHeader}>
                            <h4>English</h4>
                            <Button
                              type="secondary"
                              label="Translate"
                              disabled={loadingTranslation}
                              onClick={() => handleTranslate(channel.key, true)}
                            />
                          </div>
                          <Form.Item
                            label="Channel Name (English)"
                            name={[
                              'defaultChannels',
                              `${channel.key}_channel_EN`,
                            ]}
                            initialValue={channel.label}
                            rules={[
                              {
                                required: true,
                                message: 'English channel name is required',
                              },
                            ]}>
                            <Input
                              placeholder="Enter channel name in English"
                              className={styles.compactInput}
                            />
                          </Form.Item>
                        </div>

                        <div className={styles.languageSection}>
                          <div className={styles.languageHeader}>
                            <h4>French</h4>
                          </div>
                          <Form.Item
                            label="Channel Name (French)"
                            name={[
                              'defaultChannels',
                              `${channel.key}_channel_FR`,
                            ]}
                            initialValue={channel.label}
                            rules={[
                              {
                                required: true,
                                message: 'French channel name is required',
                              },
                            ]}>
                            <Input
                              placeholder="Enter channel name in French"
                              className={styles.compactInput}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Additional Channels Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Additional Channels</h3>
          <Form.List name="additionalChannels">
            {(fields, {add, remove}) => (
              <>
                {fields.map(({key, name: fieldName}) => {
                  const currentChannel =
                    form.getFieldValue(['additionalChannels', fieldName]) || {};
                  const currentIconRaw = currentChannel.icon || '';
                  const currentIcon = extractIconUrl(currentIconRaw);
                  const channelEN = currentChannel.channel?.EN || '';
                  const isUploading = uploadingIcons[fieldName] || false;

                  return (
                    <div key={key} className={styles.channelCard}>
                      <div className={styles.channelHeader}>
                        <div className={styles.channelInfo}>
                          {currentIcon && (
                            <img
                              src={currentIcon}
                              alt="Channel"
                              className={styles.channelIcon}
                            />
                          )}
                          <div className={styles.channelNamePreview}>
                            {channelEN || 'New Channel'}
                          </div>
                        </div>
                        <img
                          src={deleteIcon}
                          alt="Delete"
                          className={styles.deleteIcon}
                          onClick={() => remove(fieldName)}
                        />
                      </div>

                      <div className={styles.channelContent}>
                        <div className={styles.languagesContainer}>
                          <div className={styles.languageSection}>
                            <div className={styles.languageHeader}>
                              <h4>English</h4>
                              <Button
                                type="secondary"
                                label="Translate"
                                disabled={loadingTranslation}
                                onClick={() =>
                                  handleTranslate(fieldName, false)
                                }
                              />
                            </div>
                            <Form.Item
                              label="Channel Name (English)"
                              name={[fieldName, 'channel', 'EN']}
                              rules={[
                                {
                                  required: true,
                                  message: 'English channel name is required',
                                },
                              ]}>
                              <Input
                                placeholder="Enter channel name in English"
                                className={styles.compactInput}
                              />
                            </Form.Item>
                          </div>

                          <div className={styles.languageSection}>
                            <div className={styles.languageHeader}>
                              <h4>French</h4>
                            </div>
                            <Form.Item
                              label="Channel Name (French)"
                              name={[fieldName, 'channel', 'FR']}
                              rules={[
                                {
                                  required: true,
                                  message: 'French channel name is required',
                                },
                              ]}>
                              <Input
                                placeholder="Enter channel name in French"
                                className={styles.compactInput}
                              />
                            </Form.Item>
                          </div>
                        </div>

                        <Form.Item
                          label="Channel Icon"
                          name={[fieldName, 'icon']}
                          rules={[
                            {
                              required: true,
                              message: 'Icon is required',
                            },
                          ]}>
                          <Dragger
                            name="icon"
                            accept="image/*"
                            showUploadList={false}
                            customRequest={options =>
                              handleAdditionalChannelUpload(options, fieldName)
                            }
                            disabled={isUploading}>
                            <p className="ant-upload-drag-icon">
                              <UploadOutlined />
                            </p>
                            <p className="ant-upload-text">
                              {isUploading
                                ? 'Uploading...'
                                : currentIcon
                                ? 'Click or drag to replace icon'
                                : 'Click or drag to upload icon'}
                            </p>
                            {currentIcon && (
                              <p className="ant-upload-hint">
                                Current: {currentIcon.split('/').pop()}
                              </p>
                            )}
                          </Dragger>
                        </Form.Item>
                      </div>
                    </div>
                  );
                })}

                <div
                  onClick={() =>
                    add({
                      channel: {EN: '', FR: ''},
                      icon: '',
                    })
                  }
                  className={styles['add-button']}>
                  <img src={plusIcon} alt="Add" />
                  <h1>Add Channel</h1>
                </div>
              </>
            )}
          </Form.List>
        </div>

        <div className={styles.submitContainer}>
          <SubmitButton label="Update" />
        </div>
      </Form>

      <ConfirmModal
        openModal={openFinishModal}
        setOpenModal={() => setOpenFinishModal(false)}
        confirmText="Are you sure you want to update the referral channels?"
        title="Confirm Update"
        loading={loadingUpdate}
        handleOperation={handleSubmit}
      />
    </div>
  );
};
